import {
  Notification,
  NotificationActor,
  NotificationEntity,
  NotificationLink,
  NotificationType,
} from '../types';

export function buildNotification(params: {
  id: string;
  type: NotificationType;
  actor?: NotificationActor;
  entity?: NotificationEntity;
  link?: NotificationLink;
  title?: string;
  body?: string;
}): Notification {
  return {
    id: params.id,
    type: params.type,
    actor: params.actor,
    entity: params.entity,
    link: params.link,
    title: params.title,
    body: params.body,
    message: params.body,
    timestamp: Date.now(),
    read: false,
  };
}

type HabitLoopCopyInput = {
  kind: 'bite_window' | 'nearby_activity' | 'progress_milestone';
  species?: string;
  water?: string;
  windowLabel?: string;
  bait?: string;
  technique?: string;
  habitat?: string;
  totalAfterSave?: number;
  nextMilestone?: number;
  remaining?: number;
};

const clean = (value: unknown) => String(value ?? '').trim();

const lowerFirst = (value: string) => {
  const text = clean(value);
  if (!text) return '';
  return text.charAt(0).toLowerCase() + text.slice(1);
};

export function buildHabitLoopNotificationCopy(input: HabitLoopCopyInput): { title: string; body: string } {
  const species = clean(input.species) || 'fish';
  const water = clean(input.water);

  if (input.kind === 'bite_window') {
    const windowLabel = clean(input.windowLabel) || 'the next low-pressure window';
    const bait = clean(input.bait);
    const technique = clean(input.technique);
    const habitat = clean(input.habitat) || 'cover';

    const tactic = bait
      ? `${bait}${technique ? ` with ${lowerFirst(technique)}` : ''}`
      : `a confidence presentation near ${lowerFirst(habitat)}`;

    return {
      title: 'FishIQ read',
      body: `${species} may be setting up for ${windowLabel}. Consider ${tactic}; treat it as a read, not a guarantee.`,
    };
  }

  if (input.kind === 'nearby_activity') {
    return {
      title: 'Local signal noticed',
      body: water
        ? `FishIQ noticed recent ${species} activity around ${water}. Conditions may be worth watching.`
        : `FishIQ noticed recent ${species} activity nearby. Conditions may be worth watching.`,
    };
  }

  const nextMilestone = Number(input.nextMilestone || 0);
  const remaining = Number(input.remaining || 0);

  if (remaining === 0 && nextMilestone > 0) {
    return {
      title: 'Log pattern updated',
      body: `That ${species} brings your Roddo log to ${nextMilestone} catches. FishIQ has a little more signal to learn from now.`,
    };
  }

  if (nextMilestone > 0) {
    return {
      title: 'Quiet milestone nearby',
      body: `One more logged catch gets you to ${nextMilestone}. No rush — just a useful marker for your next trip.`,
    };
  }

  return {
    title: 'Roddo signal updated',
    body: `Your latest ${species} gave FishIQ a little more context for future reads.`,
  };
}


export type RelationshipNotificationCopyInput = {
  kind: 'friend_request' | 'friend_accepted';
  actorName?: string;
};

export function buildRelationshipNotificationCopy(input: RelationshipNotificationCopyInput): { title: string; body: string } {
  const actor = clean(input.actorName) || 'Someone';

  if (input.kind === 'friend_request') {
    return {
      title: 'Crew request',
      body: `${actor} wants to join your crew.`,
    };
  }

  return {
    title: 'Crew confirmed',
    body: `You and ${actor} are now crew.`,
  };
}
